import type { AnyRouter } from '@tanstack/react-router';

import { type RouteName, routeNameOf } from './routeName';

/**
 * Calls `listener` with the {@link routeNameOf} of each page the user views: the landing page, then each navigation
 * to another route or to the same one with other params. Returns the unsubscribe function. On `router-kit`, not
 * `index.ts`: its signature names a TanStack type. `pageViews.test.tsx` pins the rules:
 *
 * - It follows `onResolved`, the event TanStack's guide names for analytics: a page counts once it is on screen,
 *   loaded and rendered, not when the URL moves.
 * - The landing page counts once the router has loaded it: subscribe before the first render.
 * - A search- or hash-only change doesn't: filters and tabs rewrite the query string, and would inflate the counts.
 * - Push, replace, Back and Forward count when the page changes. A navigation a history blocker stops never loads.
 * - A URL that only redirects doesn't (`redirect` from `beforeLoad` or `loader`, or a navigation overtaken before it
 *   loads): the navigation that takes over is compared with the page the user was on. So the project navbar's
 *   Sourcing tab (`/projects/$projectId/sourcing`, redirecting to the overview) sends one page view, and so does a
 *   BOM that opens on its only subassembly.
 * - When `listener` runs, the browser URL is already the new one, so `window.location` readers (Segment's `page`)
 *   see it.
 */
export function subscribeToPageViews(router: AnyRouter, listener: (routeName: RouteName) => void): () => void {
    let lastPage: string | undefined;
    return router.subscribe('onResolved', () => {
        const leaf = router.state.matches.at(-1);
        // Not TanStack's `pathChanged`, which compares the URL's spelling: a query-string write from `/Parts/P1` or
        // `/parts/details/a@b` rebuilds the pathname as the route spells it (`/parts/P1`, `/parts/details/a%40b`).
        const page = leaf && `${leaf.routeId} ${leaf.pathname}`;
        // `onResolved` also fires on search- or hash-only changes, and with nothing changed (Back/Forward in a page).
        if (page === lastPage) {
            return;
        }
        lastPage = page;
        const routeName = routeNameOf(router, router.state.matches);
        // A page without a name, such as the catch-all splat, sends no page view.
        if (routeName !== undefined) {
            listener(routeName);
        }
    });
}
